import type { ValidationRejection } from "@altostra/type-validations";
import * as Sentry from "@sentry/browser";

import { FetchRequestError } from "./FetchRequestError";

export class FetchInvalidPayloadShapeError<ResponseJson = Record<string, unknown>> extends FetchRequestError<{
  result: ResponseJson;
  rejectionReasons: ValidationRejection[];
}> {
  constructor(
    endpoint: string,
    result: ResponseJson,
    rejectionReasons: ValidationRejection[],
    statusCode = 401,
    rejectionMessage?: string
  ) {
    // use 401 to prevent retries
    super(statusCode, rejectionMessage || `Invalid shape returned from "${endpoint}"`, {
      result,
      rejectionReasons,
    });
  }

  /**
   * Validate a result, logging failures and throwing an error if the result does not match the shape expected by the validator
   *
   * If no validator is specified, it will do nothing
   * @param cacheKey the endpoint name (used as a cache key by useDashboardApiQuery)
   * @param result the result returned from the API endpoint server
   * @param validator an altostra validator which will be used to validate the shape of the result
   * @param statusCode the HTTP status code the error should mock. anything > 400 and < 500 will prevent retries
   * @throws FetchInvalidPayloadShapeError if the result does not match the shape expected by the validator
   */
  static validateShape({
    cacheKey,
    result,
    validator,
    statusCode = 401,
    rejectionMessage,
    logException,
  }: {
    cacheKey: string;
    result: unknown;
    validator?: (value: unknown, rejectionReasons?: (rejection: ValidationRejection) => void) => boolean;
    rejectionMessage?: string;
    statusCode?: number;
    logException?: (error: Error) => void;
  }): void {
    if (!validator) {
      return;
    }

    const rejectionReasons: ValidationRejection[] = [];
    if (
      !validator(result, (reason) => {
        rejectionReasons.push(reason);
      })
    ) {
      const validationError = new FetchInvalidPayloadShapeError(
        cacheKey,
        result,
        rejectionReasons,
        statusCode,
        rejectionMessage
      );
      if (process.env.NODE_ENV !== "production") {
        console.group(`Validator rejection errors for call to '${cacheKey}':`);
        console.error(rejectionReasons);
        console.groupEnd();
        throw validationError;
      } else {
        // in production, log the error to Sentry but allow the app to continue
        if (logException) {
          // if we're inside an error boundary, use the error boundary's logException function, which is a wrapper
          // around Sentry.captureException that also adds the owning team and feature as tags
          logException(validationError);
        } else {
          // otherwise, log it without any additional context
          Sentry.captureException(validationError);
        }
      }
    }
  }
}
